import "./UserAccount.scss";

import clsx from "clsx";
import { useAtom } from "jotai";
import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom";

import { SlAccount } from "@/assets/icons/icons.tsx";
import { Tablist } from "@/components/Interface/Tablist/Tablist";
import { useIsInSecuritySubFlow } from "@/hooks/useIsInSecuritySubFlow";
import { useIsMobile } from "@/hooks/useIsMobile";
import { usePaymentMode } from "@/hooks/usePaymentMode";
import { usePaymentsTabLabel } from "@/hooks/usePaymentsTabLabel";
import { useQueryParam } from "@/hooks/useQueryParam";
import { useGetCustomer } from "@/services/api";
import {
  userAccountStateAtom,
  userAccountStateAtomInitialState,
} from "@/utils/atoms";

export const UserAccountHeaderCard: FC = () => {
  const { t } = useTranslation();
  const { data: customer } = useGetCustomer();

  return (
    <div className="flex flex-col gap-6 rounded-lg bg-surface-accent-ivory p-4 md:p-6">
      {/* Tablet/desktop align the icon tile and the title/customer-number block
          to the bottom edge of the header card, so everything sits flush along
          the container floor. */}
      <div className="flex flex-col items-start gap-4 md:flex-row md:items-end md:gap-6">
        <div className="flex size-24 shrink-0 items-center justify-center self-end rounded-lg bg-white md:self-auto">
          <SlAccount className="size-14" />
        </div>
        <div className="flex min-w-0 flex-col gap-3">
          <h1
            data-testid="tile-title"
            className="break-words text-5xl font-semibold leading-xs text-foreground-primary md:text-7xl"
          >
            {t("portal:user-account.title")}
          </h1>
          <div className="inline-flex w-fit items-center gap-1 rounded-full bg-surface-accent-mint px-2 py-1">
            <span className="text-xs font-semibold text-foreground-primary">
              {t("Footer.customerNumber")} {customer?.customerId}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export const UserAccount: FC = () => {
  const [mode] = useQueryParam("mode");

  const { t } = useTranslation();

  const [userAccountState, setUserAccountState] = useAtom(userAccountStateAtom);
  const { isInvoice } = usePaymentMode();
  const paymentsTabLabel = usePaymentsTabLabel();

  // Reset the userAccountState when the component mounts or unmounts
  useEffect(() => {
    setUserAccountState(userAccountStateAtomInitialState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // `editingPayment` is a global atom that isn't reset on navigation. The
  // invoice gate must match PaymentMethods so a leftover flag cannot lock tabs
  // or open the chromeless mobile shell with nothing to edit.
  const isEditingPayment = userAccountState.editingPayment && !isInvoice;

  const isEditingPersonal =
    userAccountState.editingPersonalData ||
    userAccountState.editingMail ||
    userAccountState.editingPassword ||
    isEditingPayment;

  const isChangingInboxSetting = mode === "edit";

  const isInSecurityChange = useIsInSecuritySubFlow();
  const isEditingSecurity =
    userAccountState.editingSecurity || isInSecurityChange;

  // While ANY edit is open (personal data, login data, inbox settings, or a 2FA
  // sub-flow) every tab is locked, so the user can't navigate away mid-edit and
  // silently lose their input -- including the tab they're currently on.
  const isEditing =
    isEditingPersonal || isChangingInboxSetting || isEditingSecurity;

  // On mobile a security sub-flow (2FA setup/change) is a standalone screen
  // showing only the active card, so the account header and the tab strip are
  // hidden. On tablet/desktop the flow expands inline and they stay.
  // Editing personal data / E-Mail / Passwort / inbox language / payment
  // method counts too: on mobile the edit card fills the
  // screen with a pinned title bar, so nothing above it may stay — same
  // in-place hide Documents uses for bill multi-select.
  const isMobile = useIsMobile();
  const isStandaloneMobileFlow =
    isMobile &&
    (isInSecurityChange ||
      userAccountState.editingPersonalData ||
      userAccountState.editingMail ||
      userAccountState.editingPassword ||
      isEditingPayment ||
      isChangingInboxSetting);

  return (
    <div className="flex w-full flex-col">
      {!isStandaloneMobileFlow && (
        <>
          <div className="tablet:mx-3">
            <UserAccountHeaderCard />
          </div>
          <div className="mt-gutter tablet:mx-4">
            <Tablist
              tabs={[
                {
                  target: "personal",
                  disabled: isEditing,
                  text: t("portal:user-account.tabs.personalDetails"),
                },
                {
                  target: "payments",
                  disabled: isEditing,
                  text: paymentsTabLabel,
                },
                {
                  target: "security",
                  disabled: isEditing,
                  text: t("portal:user-account.tabs.security"),
                },
                {
                  target: `inbox`,
                  disabled: isEditing,
                  text: t("portal:user-account.tabs.inbox"),
                },
              ]}
            />
          </div>
        </>
      )}
      {/* Keep a stable wrapper around <Outlet /> — toggling standalone mode
          must not remount the routed tab, or local edit state resets on
          click. Same pattern as Documents.

          Gutter / horizontal inset live on this outer shell so they are not
          cancelled by `#useraccount-container { margin-top: 0 !important }`
          (that rule only kills panel-layout's legacy mt-10). */}
      <div
        className={clsx(
          "min-w-0",
          isStandaloneMobileFlow
            ? "useraccount-container--standalone-edit"
            : "mt-gutter min-h-[560px] tablet:mx-6",
        )}
      >
        <div id="useraccount-container">
          <Outlet />
        </div>
      </div>
    </div>
  );
};
